/**
 * Hook for getting local photos with automatic updates
 *
 * This hook subscribes to photo storage changes and automatically
 * re-renders the component when photos are added or removed.
 */

import { useState, useEffect } from 'react';
import { getLocalPhotos, subscribeToPhotoChanges } from './offlinePhotoStorage';

/**
 * Hook to get local photos for an entity with automatic updates
 *
 * @param entityType - The type of entity (e.g., 'outlet', 'tpOutlet', 'trunk')
 * @param entityId - The ID of the entity
 * @returns Array of local blob URLs for the entity
 */
export function useLocalPhotos(entityType: string | undefined, entityId: string | undefined): string[] {
  // Use a counter to force re-renders when photos change
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [version, setVersion] = useState(0);

  // Get the current local photos (re-evaluated on each render due to version change)
  const localPhotos = entityType && entityId ? getLocalPhotos(entityType, entityId) : [];

  // Subscribe to photo changes
  useEffect(() => {
    if (!entityType || !entityId) {
      return undefined;
    }

    const unsubscribe = subscribeToPhotoChanges((changedEntityType, changedEntityId) => {
      // Only update if the change is for our entity
      if (changedEntityType === entityType && changedEntityId === entityId) {
        setVersion((v) => v + 1);
      }
    });

    return unsubscribe;
  }, [entityType, entityId]);

  return localPhotos;
}

/**
 * Hook to get combined photo URLs (server + local) with automatic updates
 *
 * @param serverUrls - Array of URLs from the server/cache
 * @param entityType - The type of entity
 * @param entityId - The ID of the entity
 * @returns Combined array of server URLs and local blob URLs
 */
export function useCombinedPhotos(
  serverUrls: readonly string[],
  entityType: string | undefined,
  entityId: string | undefined,
): string[] {
  const localPhotos = useLocalPhotos(entityType, entityId);

  // Filter out blob URLs from server URLs (they shouldn't be there, but just in case)
  const filteredServerUrls = serverUrls.filter((url) => !url.startsWith('blob:'));

  // Combine server URLs with local blob URLs
  return [...filteredServerUrls, ...localPhotos];
}
